/**
 * EditAccountNameModal Component
 * Modal for editing account custom display name
 * WP-FLOW-7: Account name editing feature
 */
import { useState, useEffect } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { useUpdateAccountName } from '@/hooks/useAccounts';
import { ApiClientError } from '@/services/api';
import { toast } from 'sonner';
import type { AccountWithConnection } from '@/services/accounts';

export interface EditAccountNameModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: AccountWithConnection | null;
}

export default function EditAccountNameModal({
  isOpen,
  onClose,
  account,
}: EditAccountNameModalProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const updateMutation = useUpdateAccountName();

  // Initialize with current account name when modal opens
  useEffect(() => {
    if (account && isOpen) {
      setName(account.name);
      setError(null);
    }
  }, [account, isOpen]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!account) return;

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Account name cannot be empty');
      return;
    }

    setError(null);

    updateMutation.mutate(
      { id: account.id, name: trimmedName },
      {
        onSuccess: () => {
          toast.success(`Account renamed to "${trimmedName}"`);
          handleClose();
        },
        onError: (err) => {
          if (err instanceof ApiClientError) {
            setError(err.detail);
          } else {
            setError('Failed to update account name. Please try again.');
          }
        },
      }
    );
  };

  const handleRestoreOriginal = () => {
    if (!account?.officialName) return;

    setError(null);

    // Setting the name to the official name effectively "restores" it
    // The backend will clear custom_name when the provided name matches official_name
    updateMutation.mutate(
      { id: account.id, name: account.officialName },
      {
        onSuccess: () => {
          toast.success('Account name restored to original');
          handleClose();
        },
        onError: (err) => {
          if (err instanceof ApiClientError) {
            setError(err.detail);
          } else {
            setError('Failed to restore account name. Please try again.');
          }
        },
      }
    );
  };

  if (!isOpen || !account) return null;

  const hasOriginalName = !!account.officialName;
  const isOriginalName = hasOriginalName && account.name === account.officialName;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-account-name-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header icon */}
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 mb-4">
          <EditIcon className="h-6 w-6 text-blue-600" />
        </div>

        <h2
          id="edit-account-name-modal-title"
          className="text-lg font-semibold text-gray-900 text-center mb-2"
        >
          Edit Account Name
        </h2>

        <p className="text-sm text-gray-600 text-center mb-4">
          Set a custom display name for this account
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Account Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter account name"
            maxLength={255}
            autoFocus
          />

          {hasOriginalName && (
            <p className="text-sm text-gray-500">
              Original name: <span className="font-medium">{account.officialName}</span>
            </p>
          )}

          {error && (
            <Alert variant="error" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div className="flex justify-between pt-2">
            {hasOriginalName && !isOriginalName && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleRestoreOriginal}
                disabled={updateMutation.isPending}
              >
                Restore Original
              </Button>
            )}
            <div className={`flex gap-3 ${hasOriginalName && !isOriginalName ? '' : 'ml-auto'}`}>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                isLoading={updateMutation.isPending}
                disabled={!name.trim()}
              >
                Save
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
      />
    </svg>
  );
}
